"use client";

import { FC, useContext } from "react";
import { IndexCardsContext } from "./IndexContextBlock";
import {
  ColumnConfig,
  SortableIndexTable,
} from "@/components/SortableIndexTable";
import {
  SeverityBadge,
  getSeverityFromCategories,
} from "@/components/SeverityBadge";
import { cardMapper } from "./cardMapper";
import { Card, CVECard } from "@/types/General";
import { Ordering } from "@/types";
import { createSortingMapping } from "@/helpers/sort";
import { Spinner } from "@/icons";
import Link from "@/components/Link";
import { getDataAutomationGroup } from "@/helpers";

type SortField = "date" | "updatedDate" | "fixedVersion" | "severity";

type CVEIndexTableBlockProps = {
  columnTitles: {
    severity: string;
    advisory: string;
    advisoryId: string;
    published: string;
    updated: string;
    fixedVersion: string;
  };
  showMore: {
    text: string;
    dlt?: string;
    dld?: string;
  };
  notFound?: {
    message: string;
    suggestion: string;
  };
};

export const CVEIndexBlock: FC<CVEIndexTableBlockProps> = ({
  columnTitles,
  showMore,
  notFound,
}) => {
  const {
    displayedCards,
    numberOfResults,
    onGoToNextPage,
    onSort,
    activeSortDescriptor,
    page,
    numberOfPaginatedCards,
    isLoading,
    searchExpression,
  } = useContext(IndexCardsContext);

  const currentSortField = activeSortDescriptor?.field as SortField | undefined;
  const currentSortDirection = activeSortDescriptor?.order;

  const hasMore = page * numberOfPaginatedCards < numberOfResults;

  const cveCards =
    (displayedCards
      ?.filter((card) => card.data.type === "CVE")
      .map((card) => cardMapper(card)) as (Card & CVECard)[]) ?? [];

  const handleSort = (field: SortField, order: Ordering["order"] | null) => {
    if (!order) {
      // 3rd click: clear sort, show cards in original order
      onSort(null, null);
    } else {
      const [sortFn] = createSortingMapping([{ type: "CVE", field, order }]);
      onSort(sortFn, { field, order });
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin">
          <Spinner />
        </div>
      </div>
    );
  }

  if (cveCards.length === 0 && notFound?.message) {
    const stripped = notFound.message.replace("{keyword}", "");
    const keywordIndex = notFound.message.indexOf("{keyword}");
    const [firstPart, secondPart] = [
      stripped.slice(0, keywordIndex),
      stripped.slice(keywordIndex),
    ];
    return (
      <div className="text-body-s sm:text-body-m text-swdc-black-600 w-full">
        <p className="mb-4">
          {firstPart}
          <strong>{searchExpression}</strong>
          {secondPart}
        </p>
        <div
          className="builder-text"
          dangerouslySetInnerHTML={{ __html: notFound.suggestion }}
        />
      </div>
    );
  }

  return (
    <SortableIndexTable
      cards={cveCards}
      columns={columns}
      columnTitles={columnTitles}
      showMore={showMore}
      hasMore={hasMore}
      isLoading={isLoading}
      currentSortField={currentSortField}
      currentSortDirection={currentSortDirection}
      onShowMore={onGoToNextPage}
      onSort={handleSort}
    />
  );
};

const formatDate = (dateString: string | null): string => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};

const columns: ColumnConfig[] = [
  {
    titleKey: "severity",
    width: 132,
    sortField: "severity",
    renderCell: (card) => (
      <SeverityBadge
        severity={getSeverityFromCategories(card.categorization)}
      />
    ),
  },
  {
    titleKey: "advisory",
    width: 380,
    cellClassName: "text-swdc-blue-500 font-bold group-hover:underline",
    renderCell: (card) => (
      <>
        {card.link?.url ? (
          <Link
            href={card.link?.url ?? ""}
            data-linktype={card.link?.dlt}
            data-linkdetail={card.link?.dld}
            {...getDataAutomationGroup("link")}
            className="outline-none after:absolute after:top-0 after:right-0 after:bottom-0 after:left-0 after:content-['']"
          >
            {card.link?.title ?? ""}
          </Link>
        ) : (
          <span>{card.link?.title ?? ""}</span>
        )}
      </>
    ),
  },
  {
    titleKey: "advisoryId",
    width: 180,
    renderCell: (card) => card.index,
  },
  {
    titleKey: "published",
    width: 140,
    sortField: "date",
    cellClassName: "whitespace-nowrap",
    renderCell: (card) => formatDate(card.date),
  },
  {
    titleKey: "updated",
    width: 140,
    sortField: "updatedDate",
    cellClassName: "whitespace-nowrap",
    renderCell: (card) => formatDate(card.updatedDate),
  },
  {
    titleKey: "fixedVersion",
    width: 260,
    sortField: "fixedVersion",
    renderCell: (card) => card.fixedVersion || "-",
  },
];
